import type { ReactNode } from 'react';
import classnames from 'classnames';
import { Icon, Panel } from '@sorrem/ui';
import styles from './HudPanel.module.scss';

interface HudPanelProps {
    /** Body content. It renders under the body frame, so edge-to-edge content (a map) is vignetted by it. */
    children?: ReactNode;
    /** Header text. The header is shown only when this is set. */
    title?: string;
    /** An image URL or icon element for a crest on the header's top rim. No crest without it. */
    crest?: string | JSX.Element;
    /** Footer content. The footer is shown only when this is set. */
    footer?: ReactNode;
    /** Hides the body; the header and footer stay visible. */
    collapsed?: boolean;
    /** Shows the minimize/restore toggle in the header when set. */
    onCollapsedChange?: (collapsed: boolean) => void;
    /**
     * Text for a tab on the panel's side that stays on screen while the panel
     * is slid off it. Shown only when set.
     */
    sideLabel?: string;
    /** Which side of the panel the label is on. */
    sideLabelSide?: 'left' | 'right';
    /** Makes the side label a button, e.g. to slide the panel back in. */
    onSideLabelClick?: () => void;
    /** Classes for the side label, such as its font size and color. */
    sideLabelClassName?: string;
    className?: string;
    bodyClassName?: string;
    footerClassName?: string;
}

/**
 * Framed in-game HUD panel: thin Panel pieces stacked into an optional
 * header and footer around a body. The body is shown only when it has
 * content, so a footer alone makes a slim strip. Collapsing animates the
 * body shut and then hides it.
 */
export function HudPanel({
    children,
    title,
    crest,
    footer,
    collapsed = false,
    onCollapsedChange,
    sideLabel,
    sideLabelSide = 'right',
    onSideLabelClick,
    sideLabelClassName,
    className,
    bodyClassName,
    footerClassName,
}: HudPanelProps): JSX.Element {
    return (
        <section className={classnames(styles.hudPanel, className, collapsed && styles.collapsed)} aria-label={title}>
            {title !== undefined && (
                <Panel variant="header" crest={crest} className={classnames(styles.header, crest !== undefined && styles.withCrest)}>
                    <h2 className={styles.title}>{title}</h2>
                    {onCollapsedChange !== undefined && (
                        // Native button: the toggle is a bare icon, not a framed Button.
                        <button
                            type="button"
                            className={styles.toggle}
                            aria-expanded={!collapsed}
                            aria-label={collapsed ? `Restore ${title}` : `Minimize ${title}`}
                            onClick={() => onCollapsedChange(!collapsed)}
                        >
                            <Icon icon={collapsed ? 'expand' : 'collapse'} />
                        </button>
                    )}
                </Panel>
            )}
            {children !== undefined && (
                <Panel variant="body" className={classnames(styles.body, bodyClassName)}>
                    {children}
                </Panel>
            )}
            {footer !== undefined && (
                <Panel variant="footer" className={classnames(styles.footer, footerClassName)}>{footer}</Panel>
            )}
            {sideLabel !== undefined && (
                // A div, not a button: it is a mouse shortcut for a panel that has
                // its own key, so it stays out of the Tab order and never takes
                // focus (focus would keep the game from receiving keys).
                <div
                    className={classnames(styles.sideLabel, styles[sideLabelSide], onSideLabelClick !== undefined && styles.clickable, sideLabelClassName)}
                    role={onSideLabelClick === undefined ? undefined : 'button'}
                    aria-label={onSideLabelClick === undefined ? undefined : sideLabel}
                    aria-hidden={onSideLabelClick === undefined ? true : undefined}
                    onClick={onSideLabelClick}
                >
                    <span className={styles.sideLabelStart} aria-hidden="true" />
                    <span className={styles.sideLabelCenter} aria-hidden="true">
                        <span className={styles.sideLabelText}>{sideLabel}</span>
                    </span>
                    <span className={styles.sideLabelEnd} aria-hidden="true" />
                </div>
            )}
        </section>
    );
}
